import React, { useState, useEffect } from 'react';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import { Pencil, Truck, FileText, FileX2, Trash2, Info, Tag } from 'lucide-react';
import ActionMenu from '../common/ActionMenu';
import Tooltip from '../common/Tooltip';
import SearchSelect from '../common/SearchSelect';
import { useApp } from '../../contexts/AppContext';

const CommandesList = ({
  showCommandeForm,
  setShowCommandeForm,
  editingCommande,
  setEditingCommande,
  handleCommandeSubmit,
  openCommandeForm,
  closeCommandeForm,
  deleteCommande,
  genererBonLivraison,
  genererFacture,
  genererAvoir,
  genererEtiquettes,
  canDeleteCommande,
  updatePrixDefaut,
  getDisplayClientType,
  getFilteredCommandes,
  lignesProduits,
  setLignesProduits,
  ajouterLigneProduit,
  supprimerLigneProduit,
  updateLigneProduit,
  commandeSearchTerm,
  setCommandeSearchTerm,
  commandeClientFilter,
  setCommandeClientFilter,
  expandedRows,
  toggleRowExpansion,
  updateCommandeStatut,
  saveBrouillon,
  currentPageCommandes,
  setCurrentPageCommandes,
  Pagination,
}) => {
  const {
    clients,
    produits,
    settings,
    hasWriteAccess,
    itemsPerPage,
    setItemsPerPage,
    getPaginatedItems,
  } = useApp();

  // 🔒 État local pour le client sélectionné dans le formulaire de création/édition.
  // Synchronisé avec editingCommande à l'ouverture du modal — évite de muter le state global
  // sur chaque changement de client (et garantit que SearchSelect, contrôlé, affiche bien la sélection).
  const [selectedClientId, setSelectedClientId] = useState('');
  useEffect(() => {
    if (showCommandeForm) {
      setSelectedClientId(editingCommande?.client_id || '');
    }
  }, [showCommandeForm, editingCommande]);

  // Helper : prix par défaut d'un produit en fonction du type du client sélectionné.
  const calcPrixDefaut = (produit, clientId) => {
    if (!produit) return 0;
    let prixDefaut = produit.prix_unitaire;
    const client = clients.find((c) => c.id === clientId);
    if (produit.prix_par_type && client && settings?.types_clients) {
      const typeIndex = settings.types_clients.findIndex(
        (type) => type.toLowerCase() === (client.type_client || '').toLowerCase()
      );
      if (typeIndex === 0) prixDefaut = produit.prix_par_type.prix_gms || produit.prix_unitaire;
      else if (typeIndex === 1) prixDefaut = produit.prix_par_type.prix_boutique || produit.prix_unitaire;
      else if (typeIndex === 2) prixDefaut = produit.prix_par_type.prix_particulier || produit.prix_unitaire;
    }
    return prixDefaut;
  };

  // Quand le client change, recalculer prix_defaut sur toutes les lignes déjà choisies.
  const handleClientChange = (val) => {
    setSelectedClientId(val);
    setLignesProduits((prev) =>
      prev.map((ligne) => {
        if (!ligne.produit_id) return ligne;
        const produit = produits.find((p) => p.id === ligne.produit_id);
        return { ...ligne, prix_defaut: calcPrixDefaut(produit, val) };
      })
    );
  };

  return (
    <div className="commandes">
      <div className="section-header">
        <h2>Gestion des Commandes</h2>
        <div className="header-buttons">
          {hasWriteAccess() && (
            <button 
              onClick={() => openCommandeForm()}
              className="btn btn-primary"
            >
              Nouvelle Commande
            </button>
          )}
        </div>
      </div>

      {showCommandeForm && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>{editingCommande ? 'Modifier la commande' : 'Nouvelle commande'}</h3>
              <button className="modal-close" onClick={() => {
                setShowCommandeForm(false);
                setEditingCommande(null);
              }}>×</button>
            </div>
            <form onSubmit={handleCommandeSubmit}>
              <div className="commande-section commande-client">
                <h4 className="section-title">👤 Informations Client</h4>
                <div className="form-group">
                  <label>Client *</label>
                  {/* Hidden input for the form submit handler (which reads via FormData) */}
                  <input
                    type="hidden"
                    name="client_id"
                    id="client-select"
                    value={selectedClientId}
                  />
                  <SearchSelect
                    testId="commande-client-select"
                    placeholder="Sélectionnez un client"
                    emptyText="Aucun client trouvé"
                    value={selectedClientId}
                    onChange={handleClientChange}
                    options={clients.map((c) => ({
                      value: c.id,
                      label: c.nom_entreprise,
                      sublabel: getDisplayClientType(c.type_client),
                      keywords: `${c.email || ''} ${c.telephone || ''} ${c.siret || ''}`,
                    }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Date de la commande {!editingCommande && '(optionnel)'}</label>
                  <input 
                    type="date" 
                    name="date_commande"
                    defaultValue={
                      editingCommande?.date_commande 
                        ? (() => {
                            const dateStr = editingCommande.date_commande;
                            if (dateStr.includes('T')) {
                              return dateStr.split('T')[0];
                            } else {
                              return dateStr;
                            }
                          })()
                        : ''
                    }
                  />
                  <small className="form-text">
                    {editingCommande 
                      ? "Date de la commande existante. Vous pouvez la modifier si nécessaire."
                      : "Si non spécifiée, la date d'aujourd'hui sera utilisée."
                    }
                  </small>
                </div>
                
                <div className="form-group">
                  <label>Commentaire (optionnel)</label>
                  <textarea 
                    name="commentaire" 
                    rows="3"
                    defaultValue={editingCommande?.commentaire || ''}
                    placeholder="N° de bon de commande, commentaire, livraison, remise, etc."
                  />
                  <small className="form-text">
                    Ce commentaire apparaîtra sur le bon de livraison, la facture et l'avoir
                  </small>
                </div>
              </div>

              <div className="commande-section commande-produits">
                <h4 className="section-title">🥬 Produits</h4>
                <div className="produits-table">
                  <div className="produits-table-head">
                    <span className="col-num">#</span>
                    <span className="col-produit">Produit</span>
                    <span className="col-qte">Quantité</span>
                    <span className="col-prix">Prix HT ({getCurrencySymbol(settings?.devise)})</span>
                    <span className="col-total">Total HT</span>
                    <span className="col-actions"></span>
                  </div>

                  {lignesProduits.map((ligne, index) => {
                    const produit = produits.find((p) => p.id === ligne.produit_id);
                    const prixApplique =
                      ligne.prix_personnalise && parseFloat(ligne.prix_personnalise) > 0
                        ? parseFloat(ligne.prix_personnalise)
                        : parseFloat(ligne.prix_defaut) || 0;
                    const totalLigne = (parseFloat(ligne.quantite) || 0) * prixApplique;
                    const prixDefaut = parseFloat(ligne.prix_defaut) || 0;
                    const isCustom =
                      ligne.prix_personnalise &&
                      parseFloat(ligne.prix_personnalise) > 0 &&
                      Math.abs(parseFloat(ligne.prix_personnalise) - prixDefaut) > 0.001;

                    return (
                      <div key={ligne.id} className="produits-table-row">
                        <span className="col-num">{index + 1}</span>

                        <div className="col-produit">
                          <SearchSelect
                            testId={`ligne-produit-select-${index}`}
                            placeholder="Sélectionnez un produit"
                            emptyText="Aucun produit trouvé"
                            value={ligne.produit_id || ''}
                            onChange={(val) => {
                              const selectedProduit = produits.find((p) => p.id === val);
                              const newPrixDefaut = calcPrixDefaut(selectedProduit, selectedClientId);
                              setLignesProduits((prev) => {
                                const next = [...prev];
                                next[index] = { ...next[index], produit_id: val, prix_defaut: newPrixDefaut };
                                return next;
                              });
                            }}
                            options={produits
                              .filter((p) => !p.fin_de_saison)
                              .map((p) => ({
                                value: p.id,
                                label: `${p.nom}${p.variete ? ` (${p.variete})` : ''}`,
                                sublabel: `${formatCurrency(p.prix_unitaire, settings?.devise)}/${p.unite}`,
                                keywords: `${p.variete || ''} ${p.famille || ''}`,
                              }))}
                          />
                        </div>

                        <div className="col-qte">
                          <div className="input-with-suffix">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={ligne.quantite}
                              onChange={(e) => updateLigneProduit(index, 'quantite', e.target.value)}
                              data-testid={`ligne-quantite-${index}`}
                              placeholder="0"
                            />
                            {produit && <span className="suffix">{produit.unite}</span>}
                          </div>
                        </div>

                        <div className="col-prix">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={ligne.prix_personnalise || (prixDefaut ? prixDefaut : '')}
                            onChange={(e) => updateLigneProduit(index, 'prix_personnalise', e.target.value)}
                            data-testid={`ligne-prix-perso-${index}`}
                            placeholder="0,00"
                            className={isCustom ? 'is-custom' : ''}
                          />
                          {isCustom && (
                            <label className="prix-save-toggle" title="Mettre à jour le prix de référence du produit">
                              <input
                                type="checkbox"
                                checked={ligne.update_prix || false}
                                onChange={(e) => updateLigneProduit(index, 'update_prix', e.target.checked)}
                              />
                              <span>Mémoriser ce prix</span>
                            </label>
                          )}
                        </div>

                        <div className="col-total" data-testid={`ligne-total-${index}`}>
                          {formatCurrency(totalLigne, settings?.devise)}
                        </div>

                        <div className="col-actions">
                          {lignesProduits.length > 1 && (
                            <button
                              type="button"
                              className="btn-action btn-danger"
                              onClick={() => supprimerLigneProduit(index)}
                              title="Supprimer cette ligne"
                              data-testid={`btn-remove-ligne-${index}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    className="btn-add-ligne"
                    onClick={ajouterLigneProduit}
                    data-testid="btn-add-ligne"
                  >
                    + Ajouter un produit
                  </button>
                </div>
              </div>

              <div className="commande-section commande-frais">
                <h4 className="section-title">🚚 Frais de livraison</h4>
                <div className="form-group">
                  <label>Frais de livraison ({getCurrencySymbol(settings?.devise)})</label>
                  <input 
                    type="number" name="frais_livraison" step="0.01" min="0" 
                    defaultValue={editingCommande?.frais_livraison || 0}
                    placeholder="0.00"
                  />
                  <small className="form-text">Le prix indiqué est HT, une TVA de 20% s'ajoutera.</small>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCommande?.statut === 'brouillon' 
                    ? 'Créer la commande' 
                    : editingCommande 
                      ? 'Modifier la commande' 
                      : 'Créer la commande'}
                </button>
                {!editingCommande && (
                  <button type="button" onClick={saveBrouillon} className="btn btn-secondary" title="Sauvegarder comme brouillon">
                    Sauvegarder brouillon
                  </button>
                )}
                {editingCommande?.statut === 'brouillon' && (
                  <button type="button" onClick={saveBrouillon} className="btn btn-secondary" title="Mettre à jour le brouillon">
                    Sauvegarder brouillon
                  </button>
                )}
                <button type="button" onClick={closeCommandeForm} className="btn btn-secondary">Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group search-group">
            <label>🔍 Rechercher</label>
            <input 
              type="text"
              value={commandeSearchTerm}
              onChange={(e) => setCommandeSearchTerm(e.target.value)}
              placeholder="N° commande, facture ou avoir..."
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label>Filtrer par client</label>
            <select 
              value={commandeClientFilter} 
              onChange={(e) => setCommandeClientFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Tous les clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.nom_entreprise}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>&nbsp;</label>
            <button 
              onClick={() => {
                setCommandeSearchTerm('');
                setCommandeClientFilter('');
                setCurrentPageCommandes(1);
              }}
              className="btn btn-secondary btn-sm"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="table-hide-mobile"></th>
              <th className="table-hide-mobile">N° Commande</th>
              <th>Client</th>
              <th className="table-hide-mobile">Date</th>
              <th className="table-hide-mobile">Produits</th>
              <th>Total</th>
              <th>
                <span className="cmd-th-with-info">
                  Statut
                  <Tooltip
                    multiline
                    content="Cycle d'une commande : Brouillon (non finalisée) → En cours (commande en préparation) → En livraison (en route vers le client) → Livrée (terminée). 'Annulée' si la commande est abandonnée."
                  >
                    <span
                      className="cmd-th-info"
                      data-testid="commande-statut-info"
                    >
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(
              getFilteredCommandes(), 
              currentPageCommandes, 
              itemsPerPage
            ).map(commande => (
              <React.Fragment key={commande.id}>
                <tr>
                  <td className="table-hide-mobile">
                    <button
                      className={`mobile-details-btn ${expandedRows.has(commande.id) ? 'expanded' : ''}`}
                      onClick={() => toggleRowExpansion(commande.id)}
                      title="Voir les détails"
                    >
                      {expandedRows.has(commande.id) ? '−' : '+'}
                    </button>
                  </td>
                  <td className="table-hide-mobile">{commande.id.substring(0, 8)}</td>
                  <td>{commande.nom_client}</td>
                  <td className="table-hide-mobile">{commande.date_commande ? new Date(commande.date_commande).toLocaleDateString('fr-FR') : 'N/A'}</td>
                  <td className="table-hide-mobile">
                    <div className="produits-list">
                      {commande.produits.map((p, index) => (
                        <span key={index} className="produit-item">
                          {p.nom_produit} ({p.quantite} {p.unite})
                          {p.prix_unitaire !== p.prix_defaut && (
                            <span className="prix-personnalise">*</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{formatCurrency(commande.total, settings?.devise)}</td>
                  <td>
                    {hasWriteAccess() ? (
                      <select 
                        value={commande.statut}
                        onChange={(e) => updateCommandeStatut(commande.id, e.target.value)}
                        className={`status-select ${commande.statut}`}
                      >
                        <option value="brouillon">Brouillon</option>
                        <option value="en_cours">En cours</option>
                        <option value="livre">Livrée</option>
                        <option value="facture">Facturée</option>
                        <option value="annule">Annulée</option>
                      </select>
                    ) : (
                      <span className={`status-label ${commande.statut}`}>
                        {commande.statut === 'brouillon' ? 'Brouillon' :
                         commande.statut === 'en_cours' ? 'En cours' :
                         commande.statut === 'livre' ? 'Livrée' :
                         commande.statut === 'facture' ? 'Facturée' :
                         commande.statut === 'annule' ? 'Annulée' :
                         commande.statut}
                      </span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const items = [];
                      if (hasWriteAccess() && commande.statut !== 'facture' && commande.statut !== 'annule') {
                        items.push({
                          label: 'Modifier la commande',
                          icon: Pencil,
                          onClick: () => openCommandeForm(commande),
                          testId: `menu-cmd-edit-${commande.id}`,
                        });
                      }
                      items.push({
                        label: 'Bon de livraison',
                        icon: Truck,
                        onClick: () => genererBonLivraison(commande.id),
                        testId: `menu-cmd-bl-${commande.id}`,
                      });
                      items.push({
                        label: 'Étiquettes cagette',
                        icon: Tag,
                        onClick: () => genererEtiquettes(commande.id),
                        testId: `menu-cmd-etiquettes-${commande.id}`,
                      });
                      if ((commande.statut === 'livre' || commande.statut === 'facture') && !commande.numero_facture) {
                        items.push({
                          label: 'Générer la facture',
                          icon: FileText,
                          onClick: () => genererFacture(commande.id),
                          variant: 'success',
                          testId: `menu-cmd-facture-${commande.id}`,
                        });
                      }
                      if (commande.numero_facture) {
                        items.push({
                          label: `Facture ${commande.numero_facture}`,
                          icon: FileText,
                          onClick: () => genererFacture(commande.id),
                          variant: 'success',
                          testId: `menu-cmd-facture-view-${commande.id}`,
                        });
                      }
                      if (commande.statut === 'annule' && !commande.numero_avoir) {
                        items.push({
                          label: "Générer l'avoir",
                          icon: FileX2,
                          onClick: () => genererAvoir(commande.id),
                          variant: 'warning',
                          testId: `menu-cmd-avoir-${commande.id}`,
                        });
                      }
                      if (commande.numero_avoir) {
                        items.push({
                          label: `Avoir ${commande.numero_avoir}`,
                          icon: FileX2,
                          onClick: () => genererAvoir(commande.id),
                          variant: 'warning',
                          testId: `menu-cmd-avoir-view-${commande.id}`,
                        });
                      }
                      if (hasWriteAccess() && canDeleteCommande(commande) && commande.statut !== 'facture' && commande.statut !== 'annule') {
                        items.push({ type: 'separator' });
                        items.push({
                          label: 'Supprimer définitivement',
                          icon: Trash2,
                          onClick: () => deleteCommande(commande.id),
                          variant: 'danger',
                          testId: `menu-cmd-delete-${commande.id}`,
                        });
                      }
                      return <ActionMenu testId={`commande-actions-${commande.id}`} items={items} />;
                    })()}
                  </td>
                </tr>
                <tr className={`mobile-details-row ${expandedRows.has(commande.id) ? 'expanded' : ''}`}>
                  <td colSpan="8">
                    <div className="mobile-details-content">
                      <div className="mobile-details-grid">
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">N° Commande</span>
                          <span className="mobile-detail-value">{commande.id.substring(0, 8)}</span>
                        </div>
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Date</span>
                          <span className="mobile-detail-value">{commande.date_commande ? new Date(commande.date_commande).toLocaleDateString('fr-FR') : 'N/A'}</span>
                        </div>
                        <div className="mobile-detail-item" style={{"gridColumn": "1 / -1"}}>
                          <span className="mobile-detail-label">Produits</span>
                          <div className="mobile-detail-value">
                            <div className="produits-list">
                              {commande.produits.map((p, index) => (
                                <span key={index} className="produit-item">
                                  {p.nom_produit} ({p.quantite} {p.unite})
                                  {p.prix_unitaire !== p.prix_defaut && (<span className="prix-personnalise">*</span>)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {commande.numero_facture && (
                          <div className="mobile-detail-item">
                            <span className="mobile-detail-label">N° Facture</span>
                            <span className="mobile-detail-value">{commande.numero_facture}</span>
                          </div>
                        )}
                        {commande.numero_avoir && (
                          <div className="mobile-detail-item">
                            <span className="mobile-detail-label">N° Avoir</span>
                            <span className="mobile-detail-value">{commande.numero_avoir}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      <Pagination
        items={getFilteredCommandes()}
        currentPage={currentPageCommandes}
        setCurrentPage={setCurrentPageCommandes}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />
    </div>
  );
};

export default CommandesList;
